<template>
    <base-dialog :loading="loading" :value="dialog" :width="560" :fullscreen="isMobile"
        :title="combo ? $t('habits.assign_title', { name: combo.name }) : ''" @input="val => { if (!val) hide() }"
        @close="hide">
        <div v-if="combo" class="as-body">
            <div>
                <label class="as-label">{{ $t('habits.includes') }} <span class="as-muted">{{ habitFrequencySummary(combo.habits) }}</span></label>
                <div class="as-sum">
                    <span v-for="habit in combo.habits" :key="habit.key" class="as-chip">
                        <habit-icon :category="habit.category" :size="20" />
                        {{ habitName(habit) }}
                        <em v-if="habit.frequency.kind !== 'daily'">{{ habitFrequencyLabel(habit.frequency) }}</em>
                        <v-icon v-if="habit.alarm.enabled && combo.alarms.mode !== 'daily'" size="13">mdi-bell-outline</v-icon>
                    </span>
                </div>
            </div>

            <div>
                <label class="as-label">
                    {{ $t('habits.students') }}
                    <span class="as-muted">{{ $tc('habits.selected_count', selectedIds.length, { n: selectedIds.length }) }}</span>
                </label>
                <kimsa-text-field :value="search" @change="search = $event || ''" :placeholder="$t('common.search')"
                    prepend-inner-icon="mdi-magnify" dense />
                <div class="as-list" :class="{ 'as-list--error': error }">
                    <skeleton-loader v-if="loadingStudents" type="rows" :count="4" />
                    <template v-else>
                        <div v-for="option in visibleOptions" :key="option.key" class="as-row"
                            :class="{ 'as-row--on': isSelected(option) }" @click="toggle(option)">
                            <student-avatar :avatar="option.avatar" :name="option.name" :avatar-key="option.id" :size="30" />
                            <div class="as-name text-truncate">{{ option.name }}</div>
                            <span class="as-meta">{{ option.meta }}</span>
                            <span class="as-chk"><v-icon size="14">mdi-check</v-icon></span>
                        </div>
                        <div v-if="!visibleOptions.length" class="as-empty">{{ $t('common.not_found') }}</div>
                    </template>
                </div>
                <div v-if="error" class="as-error">{{ error }}</div>
                <div v-if="replacing" class="as-hint">{{ $t('habits.assign_replaces') }}</div>
            </div>

            <div class="as-grid">
                <div>
                    <label class="as-label">{{ $t('habits.start_date') }}</label>
                    <kimsa-date-picker solo flat dense :value="startDate" @change="startDate = $event || startDate" />
                </div>
                <div>
                    <label class="as-label">{{ $t('habits.duration') }}</label>
                    <habit-dropdown block :items="durationItems" :value="weeks" @change="weeks = $event" />
                </div>
            </div>

            <div>
                <label class="as-label">{{ $t('habits.alarms_for_students') }}</label>
                <habit-dropdown block :items="alarmItems" :value="alarmsOverride" :min-width="300"
                    @change="alarmsOverride = $event" />
                <div class="as-hint">{{ $t('habits.alarms_for_students_hint') }}</div>
            </div>
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.assign')" :loading="loading" @cancel="hide" @confirm="assign" />
        </template>
    </base-dialog>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { db } from '@/utils/Firebase'
import { UserIndex } from '@/utils/Algolia'
import { assignCombo, chunk, today } from '@/utils/Habits'
import BaseDialog from '@/components/common/BaseDialog'
import DialogActions from '@/components/common/DialogActions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField'
import KimsaDatePicker from '@/components/common/inputs/KimsaDatePicker'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import StudentAvatar from '@/components/common/StudentAvatar'
import HabitIcon from './HabitIcon.vue'
import HabitDropdown from './HabitDropdown.vue'
import habitsMixin from '@/mixins/habitsMixin'

export default {
    name: 'AssignHabitComboDialog',
    mixins: [habitsMixin],
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaDatePicker, SkeletonLoader, StudentAvatar, HabitIcon, HabitDropdown },
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingStudents: false,
            combo: null,
            students: [],
            selected: [],
            activeIds: [],
            search: '',
            startDate: today(),
            weeks: 4,
            alarmsOverride: null,
            error: null,
            presetStudentId: null,
        }
    },
    computed: {
        ...mapState(['groups']),
        studentOptions(vm = this) {
            return vm.students.map(student => ({
                key: `s_${student.objectID}`,
                id: student.objectID,
                name: [student.name, student.lastName].filter(Boolean).join(' ') || student.email || '',
                avatar: student.avatar || null,
                meta: vm.groups.find(group => group.id === student.group)?.name || '',
                ids: [student.objectID],
            }))
        },
        groupOptions(vm = this) {
            const known = new Set(vm.students.map(student => student.objectID))
            return vm.groups.filter(group => group.coachId === vm.coachId).map(group => {
                const ids = (group.students || []).filter(id => known.has(id))
                return {
                    key: `g_${group.id}`,
                    id: group.id,
                    name: `${vm.$t('common.group')}: ${group.name}`,
                    avatar: null,
                    meta: vm.$tc('habits.students_count', ids.length, { n: ids.length }),
                    ids,
                }
            }).filter(group => group.ids.length)
        },
        visibleOptions(vm = this) {
            const term = vm.search.trim().toLowerCase()
            const options = [...vm.groupOptions, ...vm.studentOptions]
            if (!term) return options
            return options.filter(option => option.name.toLowerCase().includes(term))
        },
        selectedIds(vm = this) {
            const options = [...vm.groupOptions, ...vm.studentOptions]
            const ids = vm.selected.flatMap(key => options.find(option => option.key === key)?.ids || [])
            return [...new Set(ids)]
        },
        replacing(vm = this) {
            return vm.selectedIds.some(id => vm.activeIds.includes(id))
        },
        durationItems(vm = this) {
            return [1, 2, 3, 4, 6, 8, 12].map(n => ({ value: n, text: vm.$tc('habits.weeks', n, { n }) }))
                .concat([{ value: 0, text: vm.$t('habits.no_end') }])
        },
        alarmItems(vm = this) {
            const mode = vm.combo?.alarms?.mode === 'daily'
                ? `${vm.$t('habits.alarm_mode_daily')} · ${vm.formatHour(vm.combo.alarms.dailyHour)}`
                : vm.$t('habits.alarm_mode_per_habit')
            return [
                { value: null, text: vm.$t('habits.alarms_as_combo', { mode }) },
                { value: 'daily', text: vm.$t('habits.alarms_one_day') },
                { value: 'none', text: vm.$t('habits.alarms_none') },
            ]
        },
    },
    methods: {
        async show(combo, studentId = null) {
            const vm = this
            vm.combo = combo
            vm.selected = studentId ? [`s_${studentId}`] : []
            vm.presetStudentId = studentId
            vm.search = ''
            vm.error = null
            vm.startDate = today()
            vm.weeks = combo?.duration || 4
            vm.alarmsOverride = null
            vm.dialog = true
            await vm.loadStudents()
        },
        hide() {
            this.dialog = false
        },
        async loadStudents() {
            const vm = this
            await vm.withLoading(async () => {
                const { hits } = await UserIndex.search('', {
                    filters: `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`,
                    hitsPerPage: 1000,
                })
                vm.students = hits.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                const ids = hits.map(hit => hit.objectID)
                const snaps = await Promise.all(chunk(ids, 10).map(group =>
                    db.collection('habitAssignments').where('studentId', 'in', group).get()
                ))
                vm.activeIds = snaps.flatMap(snap => snap.docs)
                    .filter(doc => !doc.data().endDate || doc.data().endDate >= today())
                    .map(doc => doc.id)
            }, 'loadingStudents')
        },
        isSelected(option) {
            return this.selected.includes(option.key)
        },
        toggle(option) {
            const vm = this
            vm.error = null
            vm.selected = vm.isSelected(option)
                ? vm.selected.filter(key => key !== option.key)
                : [...vm.selected, option.key]
        },
        async assign() {
            const vm = this
            if (!vm.selectedIds.length) {
                vm.error = vm.$t('habits.error_no_students')
                return
            }
            await vm.withLoading(async () => {
                const n = await assignCombo({
                    combo: vm.combo,
                    comboId: vm.combo.id,
                    coachId: vm.coachId,
                    studentIds: vm.selectedIds,
                    startDate: moment(vm.startDate).format('YYYY-MM-DD'),
                    weeks: vm.weeks || null,
                    alarmsOverride: vm.alarmsOverride,
                })
                vm.toast(vm.$tc('habits.assigned_toast', n, { n }))
                vm.$emit('success')
                vm.hide()
            })
        },
    },
}
</script>

<style scoped>
.as-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.as-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
}

.as-muted {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: var(--v-txtMuted-base);
}

.as-sum {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.as-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px 3px 3px;
    border-radius: 16px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    color: var(--v-txtPrimary-base);
}

.as-chip em {
    font-style: normal;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-accent-base);
}

.as-chip .v-icon {
    color: var(--v-accent-base) !important;
}

.as-list {
    margin-top: 8px;
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 4px;
}

.as-list--error {
    border-color: var(--v-error-base);
}

.as-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background .15s;
}

.as-row:hover {
    background: var(--v-surface2-base);
}

.as-name {
    flex: 1;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
}

.as-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.as-chk {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1px solid var(--v-outline2-base);
    display: flex;
    align-items: center;
    justify-content: center;
}

.as-chk .v-icon {
    color: transparent !important;
}

.as-row--on .as-chk {
    background: var(--v-accent-base);
    border-color: var(--v-accent-base);
}

.as-row--on .as-chk .v-icon {
    color: #fff !important;
}

.as-empty {
    padding: 16px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.as-error {
    margin-top: 4px;
    font-size: 12px;
    color: var(--v-error-base);
}

.as-hint {
    margin-top: 6px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.as-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

@media (max-width: 700px) {
    .as-grid {
        grid-template-columns: 1fr;
    }
}
</style>
